import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Alert } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { AlertDialog, RobotoButton } from "@/shared/components";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { useDeleteSession } from "@/shared/domain/sessions/hooks";
import { formatNanoDuration, formatNanoTimestamp } from "@/shared/time";

import styles from "./SessionDetailsTab.module.css";
import { SessionMetadata } from "./SessionMetadata";
import { SessionTags } from "./SessionTags";
import { useSessionOutletContext } from "./useSessionOutletContext";

const PLACEHOLDER = "—";

export function SessionDetailsTab() {
  const { session } = useSessionOutletContext();
  const { goto } = useNavigation();
  const { mutateAsync: deleteSession, error: deleteError } = useDeleteSession();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  return (
    <div className={styles.root}>
      <DeleteErrorAlert error={deleteError} />

      <section className={styles.card}>
        <h3 className={styles.cardHeading}>Properties</h3>
        <dl className={styles.propertyList}>
          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Session ID</dt>
            <dd className={styles.propertyValueMono}>{session.session_id}</dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Duration</dt>
            <dd className={styles.propertyValue}>
              {session.min_timestamp_ns !== null &&
              session.max_timestamp_ns !== null
                ? formatNanoDuration(
                    session.max_timestamp_ns - session.min_timestamp_ns,
                  )
                : PLACEHOLDER}
            </dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Started</dt>
            <dd className={styles.propertyValue}>
              {session.min_timestamp_ns !== null
                ? formatNanoTimestamp(session.min_timestamp_ns)
                : PLACEHOLDER}
            </dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Tags</dt>
            <dd className={styles.propertyValue}>
              <SessionTags session={session} />
            </dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Metadata</dt>
            <dd className={styles.propertyValue}>
              <SessionMetadata session={session} />
            </dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Created</dt>
            <dd className={styles.propertyValue}>
              {session.created ? (
                <>
                  {new Date(session.created).toLocaleString()}
                  {" by "}
                  <RobotoPrincipalUnderlineLink
                    className={styles.inlinePrincipal}
                    principal={session.created_by}
                  />
                </>
              ) : (
                PLACEHOLDER
              )}
            </dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Updated</dt>
            <dd className={styles.propertyValue}>
              {session.modified ? (
                <>
                  {new Date(session.modified).toLocaleString()}
                  {" by "}
                  <RobotoPrincipalUnderlineLink
                    className={styles.inlinePrincipal}
                    principal={session.modified_by}
                  />
                </>
              ) : (
                PLACEHOLDER
              )}
            </dd>
          </div>
        </dl>
      </section>

      <div className={styles.operations}>
        <span className={styles.operationsLabel}>Operations</span>
        <div>
          <RobotoButton
            eventName="DeleteSessionClicked"
            eventProperties={{ sessionId: session.session_id }}
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteOutlinedIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete Session
          </RobotoButton>
        </div>
      </div>

      <AlertDialog
        dialogOpen={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        dialogTitle="Delete Session"
        dialogText={`Are you sure you want to delete ${session.name ? `${session.name} (${session.session_id})` : session.session_id}?`}
        dialogAction={async () => {
          await deleteSession({
            sessionId: session.session_id,
            resourceOwnerId: session.org_id,
          });
          goto.sessions();
        }}
      />
    </div>
  );
}

function DeleteErrorAlert({ error }: { error: Error | null }) {
  if (!error) {
    return null;
  }
  return (
    <Alert severity="error" className={styles.errorAlert}>
      {error.message}
    </Alert>
  );
}
